import { Outlet, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { logoutCliente } from "../../services/LoginUserCliente";
import { getPedidosCliente } from "../../services/usuarios_cliente/perfilpedidos";
import { getDireccionesCliente } from "../../services/usuarios_cliente/perfildirecciones";
import { getPerfilCliente } from "../../services/usuarios_cliente/perfilcliente";
import ToastStack from "../../components/ToastStack";
import OrdersModal from "./OrdersModal";
import OrderDocsModal from "./OrderDocsModal";
import "./AccountLayout.css";
import { getErrMsg, norm, onlyDigits } from "../../utils/accountUtils";

export default function AccountLayout() {
  const navigate = useNavigate();

  const [profile, setProfile] = useState({
    nombre: "",
    apellido: "",
    correo: "",
    codigo_telefono: "+502",
    telefono: "",
  });

  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);

  const [loadingPerfil, setLoadingPerfil] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(true);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDocs, setShowDocs] = useState(false);
  const [docsOrderId, setDocsOrderId] = useState(null);

  const [toasts, setToasts] = useState([]);
  const toastTimers = useRef(new Map());

  const pushToast = (type, title, message, opts = {}) => {
    const id = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const duration = Number.isFinite(opts.duration) ? opts.duration : 4200;

    const toast = { id, type, title, message };
    setToasts((prev) => [toast, ...prev].slice(0, 4));

    const timer = window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      toastTimers.current.delete(id);
    }, duration);

    toastTimers.current.set(id, timer);
    return id;
  };

  const closeToast = (id) => {
    const timer = toastTimers.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      toastTimers.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  useEffect(() => {
    const timers = toastTimers.current;
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
    };
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoadingPerfil(true);
      setLoadingOrders(true);

      const results = await Promise.allSettled([
        getPerfilCliente(),
        getDireccionesCliente(),
        getPedidosCliente(),
      ]);

      const [perfilRes, direccionesRes, pedidosRes] = results;

      if (perfilRes.status === "fulfilled") {
        const p = perfilRes.value?.data || {};

        const loaded = {
          nombre: p?.nombre || "",
          apellido: p?.apellido || "",
          correo: p?.correo || "",
          codigo_telefono: p?.codigo_telefono || "+502",
          telefono: onlyDigits(p?.telefono || ""),
        };

        setProfile({
          nombre: norm(loaded.nombre),
          apellido: norm(loaded.apellido),
          correo: norm(loaded.correo),
          codigo_telefono: norm(loaded.codigo_telefono) || "+502",
          telefono: norm(loaded.telefono),
        });
      } else {
        pushToast(
          "error",
          "Error al cargar perfil",
          getErrMsg(
            perfilRes.reason,
            "No pudimos cargar los datos de tu perfil."
          )
        );
      }

      if (direccionesRes.status === "fulfilled") {
        setAddresses(
          Array.isArray(direccionesRes.value?.data)
            ? direccionesRes.value.data
            : []
        );
      } else {
        setAddresses([]);
        pushToast(
          "error",
          "Error al cargar direcciones",
          getErrMsg(
            direccionesRes.reason,
            "No pudimos cargar tus direcciones."
          )
        );
      }

      if (pedidosRes.status === "fulfilled") {
        setOrders(
          Array.isArray(pedidosRes.value?.data)
            ? pedidosRes.value.data
            : []
        );
      } else {
        setOrders([]);
        pushToast(
          "error",
          "Error al cargar pedidos",
          getErrMsg(
            pedidosRes.reason,
            "No pudimos cargar tus pedidos."
          )
        );
      }

      setLoadingPerfil(false);
      setLoadingOrders(false);
    };

    loadAll();
  }, []);

  const fullName = useMemo(() => {
    return profile.nombre || profile.apellido
      ? `${profile.nombre} ${profile.apellido}`.trim()
      : "Cliente";
  }, [profile.nombre, profile.apellido]);

  const handleBackToStore = () => {
    navigate("/store/inicio");
  };

  const handleLogout = () => {
    logoutCliente();
    navigate("/cliente/login", { replace: true });
  };

  return (
    <div className="adc-account-scope adc-account-theme-neutral">
      <div className="adc-account-css-root">
        <ToastStack toasts={toasts} onClose={closeToast} />

        <header className="adc-account-header">
          <div className="adc-header-row">
            <div className="adc-header-left">
              <h1 className="adc-title">Mi cuenta</h1>
              <p className="adc-subtitle">
                {loadingPerfil ? "Cargando información..." : <>Hola, <strong>{fullName}</strong></>}
              </p>
            </div>

            <div className="adc-header-actions">
              <button
                className="adc-btn adc-btn-secondary"
                type="button"
                onClick={handleBackToStore}
              >
                Regresar a la tienda
              </button>

              <button
                className="adc-btn adc-btn-danger"
                type="button"
                onClick={handleLogout}
              >
                Cerrar sesión
              </button>
            </div>
          </div>
        </header>

        <main className="adc-account-shell">
          <Outlet
            context={{
              profile,
              setProfile,
              orders,
              setOrders,
              addresses,
              setAddresses,
              loadingPerfil,
              loadingOrders,
              pushToast,
              fullName,
              openOrderDetails: (pedidoId) => setSelectedOrder(pedidoId),
              openOrderDocs: (pedidoId) => {
                setDocsOrderId(pedidoId);
                setShowDocs(true);
              },
            }}
          />
        </main>

        {selectedOrder && (
          <OrdersModal
            pedidoId={selectedOrder}
            onClose={() => setSelectedOrder(null)}
          />
        )}
      </div>

      {showDocs && docsOrderId && (
        <OrderDocsModal
          pedidoId={docsOrderId}
          onClose={() => {
            setShowDocs(false);
            setDocsOrderId(null);
          }}
        />
      )}
    </div>
  );
}